import { SessionStatusBot } from '@hyper-inferno/survey-elements-core';
import {
  ISurveySession,
  SurveySessionStatus,
  SyncStatus,
} from './survey-session.interface';

export class SurveySession implements ISurveySession {
  id!: string;
  survey_id!: number;
  session_status!: SurveySessionStatus;
  current_page_id!: number | undefined;
  survey_language!: string;
  survey_version!: number;

  jsn_responses!: Record<string, any>;
  jsn_session_state!: Record<string, any>;
  jsn_activity_order!: any[];
  jsn_constructed_lists!: Record<string, any>;
  jsn_oe_mapping!: Record<string, any>;
  jsn_passive_media!: any[];

  sys_survey_start_time!: string;
  sys_survey_end_time?: string;
  sys_last_updated!: string;
  sys_profile_identifier!: string | null;
  sys_disposition_code!: number | null;
  sys_survey_delivery_status!: string | null;
  sys_jsn_device_data!: Record<string, any>;

  updated_at!: string;
  sync_status!: SyncStatus;
  last_sync_time?: string;

  constructor(data: Partial<ISurveySession>) {
    // 1. Map core fields with defaults
    this.id = data.id || crypto.randomUUID();
    this.survey_id = data.survey_id || 0;
    this.session_status =
      data.session_status || SurveySessionStatus.IN_PROGRESS;
    this.current_page_id = data.current_page_id;
    this.survey_language = data.survey_language || 'en';
    this.survey_version = data.survey_version || 1;

    // 2. Map JSONB objects
    this.jsn_responses = data.jsn_responses || {};
    this.jsn_session_state = data.jsn_session_state || {};
    this.jsn_activity_order = data.jsn_activity_order || [];
    this.jsn_constructed_lists = data.jsn_constructed_lists || {};
    this.jsn_oe_mapping = data.jsn_oe_mapping || {};
    this.jsn_passive_media = data.jsn_passive_media || [];

    // 3. Map System fields
    this.sys_survey_start_time =
      data.sys_survey_start_time || new Date().toISOString();
    this.sys_survey_end_time = data.sys_survey_end_time;
    this.sys_last_updated = data.sys_last_updated || new Date().toISOString();
    this.sys_profile_identifier = data.sys_profile_identifier || null;
    this.sys_disposition_code = data.sys_disposition_code || null;
    this.sys_survey_delivery_status = data.sys_survey_delivery_status || null;
    this.sys_jsn_device_data = data.sys_jsn_device_data || {};

    // 4. Sync metadata
    this.updated_at = data.updated_at || new Date().toISOString();
    this.sync_status = data.sync_status || SyncStatus.DIRTY;
    this.last_sync_time = data.last_sync_time;
  }

  // --- Logic / Business Rules ---

  /**
   * Internal helper to mark the record as modified.
   * This triggers the need for a sync push.
   */
  private markAsDirty() {
    this.updated_at = new Date().toISOString();
    this.sync_status = SyncStatus.DIRTY;
    this.sys_last_updated = new Date().toISOString();
  }

  // answerQuestion(questionId: string, answer: any) {
  //   this.jsn_responses = { ...this.jsn_responses, [questionId]: answer };
  //   this.markAsDirty();
  // }

  moveToQuestion(pageId: number) {
    if (this.current_page_id === pageId) return;
    this.current_page_id = pageId;
    this.markAsDirty();
  }

  endSurvey(status: SessionStatusBot, disposition_code = 'SURVEY_END') {
    switch (status) {
      case SessionStatusBot.COMPLETED:
        this.complete();
        break;

      case SessionStatusBot.TERMINATED:
        this.terminate(disposition_code);
        break;

      default:
        break;
    }
  }

  complete() {
    this.session_status = SurveySessionStatus.COMPLETED;
    this.sys_survey_end_time = new Date().toISOString();
    this.markAsDirty();
  }

  terminate(disposition_code: string) {
    this.session_status = SurveySessionStatus.TERMINATED;
    this.sys_survey_end_time = new Date().toISOString();
    // this.sys_disposition_code = disposition_code;
    this.markAsDirty();
  }

  markSynced() {
    this.sync_status = SyncStatus.SYNCED;
    this.last_sync_time = new Date().toISOString();
  }

  setLanguage(lang: string) {
    if (this.session_status !== SurveySessionStatus.IN_PROGRESS) return;
    if (this.survey_language === lang) return;
    this.survey_language = lang;
    this.markAsDirty();
  }

  /**
   * Returns a plain object matching the Supabase table schema exactly.
   */
  toPersistence() {
    return {
      id: this.id,
      survey_id: this.survey_id,
      session_status: this.session_status,
      current_page_id: this.current_page_id,
      survey_language: this.survey_language,
      survey_version: this.survey_version,
      jsn_responses: this.jsn_responses,
      jsn_session_state: this.jsn_session_state,
      jsn_activity_order: this.jsn_activity_order,
      jsn_constructed_lists: this.jsn_constructed_lists,
      jsn_oe_mapping: this.jsn_oe_mapping,
      jsn_passive_media: this.jsn_passive_media,
      sys_survey_start_time: this.sys_survey_start_time,
      sys_survey_end_time: this.sys_survey_end_time,
      sys_last_updated: this.sys_last_updated,
      sys_profile_identifier: this.sys_profile_identifier,
      sys_disposition_code: this.sys_disposition_code,
      sys_survey_delivery_status: this.sys_survey_delivery_status,
      sys_jsn_device_data: this.sys_jsn_device_data,
    };
  }
}
